import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, FileText, Cookie } from 'lucide-react';
import Navigation from './Navigation';
import { useLocation } from 'react-router-dom';

interface PolicyPageProps {
  type: 'privacy' | 'terms' | 'cookies';
}

const PolicyPage: React.FC<PolicyPageProps> = ({ type }) => {
  const location = useLocation();

  // Scroll to top when component mounts or route changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const getPolicyContent = () => {
    switch (type) {
      case 'privacy':
        return {
          title: 'Privacy Policy',
          icon: <Shield className="w-8 h-8 text-green-600" />,
          lastUpdated: 'August 14, 2024',
          content: [
            {
              section: 'Information We Collect',
              items: [
                'Account information (name, email, profile details)',
                'Measurement data and sizing information',
                'Order details and preferences',
                'Authentication data from OAuth providers',
                'Technical information and usage analytics'
              ]
            },
            {
              section: 'How We Use Your Information',
              items: [
                'Provide and maintain our tailoring platform',
                'Process orders and manage your account',
                'Connect you with tailors and manage measurements',
                'Send order updates and service notifications',
                'Improve our platform and user experience'
              ]
            },
            {
              section: 'Data Sharing and Disclosure',
              items: [
                'Share measurements with authorized tailors',
                'Provide contact information for order coordination',
                'Enable communication between customers and tailors',
                'Comply with legal requirements and regulations',
                'Protect against fraud and security threats'
              ]
            },
            {
              section: 'Data Security',
              items: [
                'End-to-end encryption for sensitive data',
                'Secure authentication and access controls',
                'Regular security audits and monitoring',
                'GDPR compliance and data protection',
                'Secure hosting and infrastructure'
              ]
            }
          ]
        };
      case 'terms':
        return {
          title: 'Terms of Service',
          icon: <FileText className="w-8 h-8 text-brand-blue" />,
          lastUpdated: 'August 14, 2024',
          content: [
            {
              section: 'Acceptance of Terms',
              items: [
                'By using MasterJi, you agree to these terms',
                'You must be at least 18 years old to use our services',
                'You are responsible for maintaining account security',
                'You agree to provide accurate and complete information',
                'You must comply with all applicable laws and regulations'
              ]
            },
            {
              section: 'Service Description',
              items: [
                'MasterJi provides a digital marketplace for tailoring services',
                'We connect customers with verified tailors and fashion houses',
                'We offer measurement storage and order management tools',
                'Our platform facilitates communication and transactions',
                'We provide business management tools for tailors'
              ]
            },
            {
              section: 'User Responsibilities',
              items: [
                'Maintain accurate and up-to-date profile information',
                'Respect intellectual property and copyright laws',
                'Use the platform for lawful purposes only',
                'Maintain appropriate security for your account',
                'Report any suspicious or inappropriate activity'
              ]
            },
            {
              section: 'Limitation of Liability',
              items: [
                'MasterJi is not responsible for tailoring work quality',
                'We do not guarantee specific outcomes or results',
                'Our liability is limited to the amount paid for services',
                'We are not liable for indirect or consequential damages',
                'Force majeure events may affect service availability'
              ]
            }
          ]
        };
      case 'cookies':
        return {
          title: 'Cookie Policy',
          icon: <Cookie className="w-8 h-8 text-brand-yellow" />,
          lastUpdated: 'August 14, 2024',
          content: [
            {
              section: 'What Are Cookies',
              items: [
                'Cookies are small text files stored on your device',
                'They help us provide a better user experience',
                'Cookies remember your preferences and settings',
                'They enable essential website functionality',
                'Some cookies are necessary for security purposes'
              ]
            },
            {
              section: 'Types of Cookies We Use',
              items: [
                'Essential cookies for basic website functionality',
                'Authentication cookies for secure login sessions',
                'Preference cookies to remember your settings',
                'Analytics cookies to improve our services',
                'Marketing cookies for personalized content'
              ]
            },
            {
              section: 'Cookie Management',
              items: [
                'You can control cookies through your browser settings',
                'Most browsers allow you to block or delete cookies',
                'Disabling cookies may affect website functionality',
                'You can opt out of non-essential cookies',
                'Cookie preferences can be updated at any time'
              ]
            },
            {
              section: 'Third-Party Cookies',
              items: [
                'We use trusted third-party services for analytics',
                'Social media platforms may set their own cookies',
                'Payment processors use cookies for security',
                'We do not control third-party cookie policies',
                'You can opt out of third-party cookies'
              ]
            }
          ]
        };
      default:
        return {
          title: 'Policy',
          icon: <FileText className="w-8 h-8 text-gray-600" />,
          lastUpdated: 'August 14, 2024',
          content: []
        };
    }
  };

  const policy = getPolicyContent();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-brand-white to-brand-blue/10">
      {/* Header */}
      <Navigation />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          <div className="prose prose-lg max-w-none">
            <div className="mb-8">
              <p className="text-gray-600 leading-relaxed">
                This {policy.title.toLowerCase()} explains how MasterJi collects, uses, and protects your information when you use our tailoring platform. 
                We are committed to transparency and protecting your privacy and rights.
              </p>
            </div>

            {policy.content.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="mb-8"
              >
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">{section.section}</h2>
                <ul className="space-y-3">
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-brand-blue rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}

            <div className="mt-12 p-6 bg-gray-50 rounded-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Us</h3>
              <p className="text-gray-600 mb-4">
                If you have any questions about this {policy.title.toLowerCase()}, please contact us:
              </p>
              <div className="space-y-2 text-sm text-gray-600">
                <p><strong>Email:</strong> infotherealmasterji@gmail.com</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PolicyPage;

